/*
  Plain-language text for undo history entries (shell toast, Recent changes, confirm dialog).

    humanizeUndoLabel('Create project Waldorf Astoria') → 'Project “Waldorf Astoria” created'
    humanizeUndoLabel('Delete project Waldorf Astoria') → 'Project “Waldorf Astoria” deleted'
    humanizeUndoLabel('Delete 3 shop drawings')         → '3 shop drawings deleted'
    humanizeUndoLabel('Update stage status')            → 'Stage status updated'
    humanizeUndoLabel('Restore Create project X')       → 'Undid: Project “X” created'

  buildUndoConfirm(action) → options for useConfirm() that say exactly what undoing will do, from the
  backend's `action.effect` ({ removes, restores, reverts, requires_confirmation }).
*/
import { FilePlus2, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/lib/format';

// Record nouns that may follow "Create …" in backend labels, longest first.
const NOUNS = [
  'variation invoice', 'delivery note', 'site request', 'catalog item', 'stock movement', 'stock item',
  'shop drawing', 'BOQ item', 'reservation', 'variation', 'document', 'invoice', 'project', 'task', 'user', 'LPO',
];

const VERBS = {
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  import: 'imported',
  reorder: 'reordered',
  merge: 'merged',
  duplicate: 'duplicated',
  complete: 'completed',
  move: 'moved',
  change: 'changed',
  reset: 'reset',
  disconnect: 'disconnected',
  log: 'logged',
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

export function humanizeUndoLabel(rawLabel) {
  const label = String(rawLabel || '').trim();
  if (!label) return 'Change';

  if (/^restore\s+/i.test(label)) {
    const rest = label.replace(/^restore\s+/i, '');
    if (/^step\s+/i.test(rest)) return `Restored ${rest}`;
    return `Undid: ${humanizeUndoLabel(rest)}`;
  }

  const multiWord = label.match(/^(bulk update|inline edit)\s+(.+)$/i);
  if (multiWord) return `${capitalize(multiWord[2])} ${multiWord[1].toLowerCase() === 'bulk update' ? 'updated' : 'edited'}`;

  const mark = label.match(/^mark\s+(\S+)\s+(.+)$/i);
  if (mark) return `${capitalize(mark[1])} marked ${mark[2]}`;

  const match = label.match(/^(\S+)\s+(.+)$/);
  if (!match) return label;
  const verb = VERBS[match[1].toLowerCase()];
  if (!verb) return label;
  const rest = match[2];

  // "Create project Waldorf Astoria" / "Delete project Waldorf Astoria" → Project “Waldorf Astoria” …
  // A bare noun ("Delete variation invoice") has no name to quote — it must not read as variation “invoice”.
  const bareNoun = NOUNS.some((candidate) => rest.toLowerCase() === candidate.toLowerCase());
  if (!bareNoun && ['create', 'delete'].includes(match[1].toLowerCase())) {
    const noun = NOUNS.find((candidate) => rest.toLowerCase().startsWith(`${candidate.toLowerCase()} `));
    if (noun) {
      const name = rest.slice(noun.length).trim();
      return `${capitalize(noun)} “${name}” ${verb}`;
    }
  }
  return `${capitalize(rest)} ${verb}`;
}

const quoteLabels = (labels = [], count = 0) => {
  const named = labels.filter(Boolean).map((label) => `“${label}”`);
  if (!named.length) return '';
  const more = count - named.length;
  return more > 0 ? `${named.join(', ')} +${more} more` : named.join(', ');
};

function describeGroup(verb, group) {
  const names = quoteLabels(group.labels, group.count);
  if (group.count === 1) return `${verb} ${group.noun} ${names}`.trim();
  return `${verb} ${group.count} ${group.noun}${names ? ` (${names})` : ''}`;
}

/** Bullet lines ("Delete project “Waldorf Astoria”", …) for what undoing `action` does. */
export function describeUndoEffect(effect) {
  if (!effect) return [];
  return [
    ...(effect.removes || []).map((group) => ({ icon: Trash2, label: describeGroup('Delete', group) })),
    ...(effect.restores || []).map((group) => ({ icon: RotateCcw, label: describeGroup('Bring back', group) })),
    ...(effect.reverts || []).map((group) => ({ icon: Pencil, label: describeGroup('Revert the edit to', group) })),
  ];
}

/** Project / record the change belongs to — '' when the label already names it. */
export function undoWhere(action) {
  const entity = String(action?.entity_label || '').trim();
  const where = action?.project_label || (['', 'Unknown', 'Project'].includes(entity) ? '' : entity);
  if (!where) return '';
  return humanizeUndoLabel(action?.label).toLowerCase().includes(where.toLowerCase()) ? '' : where;
}

export const isRedo = (action) => /^restore\s+(?!step\s)/i.test(String(action?.label || '').trim());

/** Options for useConfirm(): title, when/where, and exactly what will be deleted / brought back / reverted. */
export function buildUndoConfirm(action) {
  const effect = action?.effect || null;
  const removes = effect?.remove_count || 0;
  const deletesProject = (effect?.removes || []).some((group) => group.collection === 'projects');
  const consequences = describeUndoEffect(effect);
  const when = action?.created_at ? formatDateTime(action.created_at, { relativeDay: true }) : '';
  const where = undoWhere(action);
  const redo = isRedo(action);
  return {
    title: redo ? 'Redo this change?' : 'Undo this change?',
    subtitle: [humanizeUndoLabel(action?.label), where, when].filter(Boolean).join(' · '),
    description: consequences.length
      ? 'This will:'
      : 'This puts the affected records back the way they were before the change.',
    consequences,
    icon: removes ? Trash2 : redo ? FilePlus2 : RotateCcw,
    tone: removes ? 'danger' : 'default',
    confirmLabel: deletesProject ? 'Undo and delete project' : removes ? (removes === 1 ? "Undo and delete it" : `Undo and delete ${removes} records`) : redo ? 'Redo' : 'Undo change',
    note: 'If anyone edited these records since, the undo is blocked and nothing changes.',
  };
}
